import ApplicationController from './application_controller';

export default class extends ApplicationController {
  static values = { totalRequiredErrors: Number };

  connect() {
    this.fixedRequiredErrors = new Set();
    this.fixedOptionalWarnings = new Set();
    this.inputDebounceTimers = {};
    this.selectizeHandlers = new Map();

    this._onClick = this._handleClick.bind(this);
    this._onChange = this._handleChange.bind(this);
    this._onFocusOut = this._handleFocusOut.bind(this);
    this._onInput = this._handleInput.bind(this);

    this.element.addEventListener('click', this._onClick);
    this.element.addEventListener('change', this._onChange);
    this.element.addEventListener('focusout', this._onFocusOut);
    this.element.addEventListener('input', this._onInput);

    this._scheduleSelectizeHooks();
    this._updateSubmitButton();
  }

  disconnect() {
    this.element.removeEventListener('click', this._onClick);
    this.element.removeEventListener('change', this._onChange);
    this.element.removeEventListener('focusout', this._onFocusOut);
    this.element.removeEventListener('input', this._onInput);

    if (this.selectizeRetryTimers) {
      this.selectizeRetryTimers.forEach((id) => clearTimeout(id));
    }
    Object.values(this.inputDebounceTimers || {}).forEach((id) => clearTimeout(id));

    this.selectizeHandlers.forEach((handler, el) => {
      if (el.selectize) el.selectize.off('change', handler);
    });
    this.selectizeHandlers.clear();
  }

  // --- Event handlers ---

  _handleClick(event) {
    const link = event.target.closest('a[href^="#patient-row-"]');
    if (!link) return;
    event.preventDefault();
    const target = document.getElementById(link.getAttribute('href').substring(1));
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.style.transition = 'background-color 0.3s ease';
    const original = target.style.backgroundColor;
    target.style.backgroundColor = '#fff3cd';
    setTimeout(() => { target.style.backgroundColor = original; }, 1000);
  }

  _handleChange(event) {
    const el = event.target;

    if (el.matches('.sex-radio')) {
      const row = el.closest('[id^="sex-field-"]');
      if (row) this._validateSexRow(row.id.replace('sex-field-', ''));
      return;
    }

    if (el.matches('select.validation-field')) {
      setTimeout(() => {
        this._validateField(el);
        if (el.dataset.field === 'discipline' || el.dataset.field === 'diagnosis codes') {
          const row = el.closest('.nested-fields');
          if (row) {
            const other = el.dataset.field === 'discipline'
              ? row.querySelector('select[id^="p_diagnosis_codes_"]')
              : row.querySelector('select[id^="p_discipline_"]');
            if (other && other !== el) this._validateField(other);
          }
        }
      }, 0);
    }
  }

  _handleFocusOut(event) {
    const el = event.target;
    if (el.matches('.validation-field')) this._validateField(el);
  }

  _handleInput(event) {
    const el = event.target;
    if (!el.matches('input.validation-field')) return;
    const t = el.type;
    if (t !== 'text' && t !== 'email' && t !== 'tel') return;
    const id = el.id || Math.random().toString(36);
    clearTimeout(this.inputDebounceTimers[id]);
    this.inputDebounceTimers[id] = setTimeout(() => this._validateField(el), 500);
  }

  // --- Field validation ---

  _validateField(field) {
    const { line, field: fieldName, severity } = field.dataset;

    if (severity === 'required' && field.dataset.hasError === 'true') {
      const value = (field.value || '').trim();
      let isValid = false;

      switch (fieldName) {
        case 'first name':
        case 'last name':
          isValid = value.length > 0;
          break;
        case 'date of birth':
          isValid = /^\d{2}-\d{2}-\d{4}$/.test(value);
          break;
        case 'patient status':
        case 'time zone':
          isValid = this._selectizeValue(field).length > 0;
          break;
        default:
          isValid = value.length > 0;
      }

      if (isValid) {
        field.classList.remove('is-invalid');
        field.classList.add('is-valid');
        this._findRequiredErrorTextsForLineField(line, fieldName).forEach((t) => this._markRequiredErrorFixed(t));
      } else {
        field.classList.remove('is-valid');
        field.classList.add('is-invalid');
      }
    }

    if (severity === 'optional' && field.dataset.hasWarning === 'true') {
      const value = (field.value || '').trim();
      const rowEl = field.closest('.nested-fields');

      if (fieldName === 'email') {
        if (value.length === 0) {
          field.classList.remove('import-input-border-warning', 'import-field-optional-warning', 'is-invalid');
          this._findOptionalWarningTextsForLine(line, 'email').forEach((t) => this._markOptionalWarningFixed(t));
        } else if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          field.classList.remove('import-input-border-warning', 'import-field-optional-warning', 'is-invalid');
          field.classList.add('is-valid', 'import-field-optional-warning');
          this._findOptionalWarningTextsForLine(line, 'email').forEach((t) => this._markOptionalWarningFixed(t));
        }
      } else if (fieldName === 'phone number') {
        if (value.length === 0) {
          field.classList.remove('import-input-border-warning', 'import-field-optional-warning', 'is-invalid');
          this._findOptionalWarningTextsForLine(line, 'phone number').forEach((t) => this._markOptionalWarningFixed(t));
        } else if (/^\d{3}-\d{3}-\d{4}$/.test(value)) {
          field.classList.remove('import-input-border-warning', 'import-field-optional-warning', 'is-invalid');
          field.classList.add('is-valid', 'import-field-optional-warning');
          this._findOptionalWarningTextsForLine(line, 'phone number').forEach((t) => this._markOptionalWarningFixed(t));
        }
      } else if (fieldName === 'payer') {
        const selectedValue = this._selectizeValue(field);
        if (selectedValue.length > 0) {
          field.classList.remove('import-input-border-warning', 'is-invalid');
          field.classList.add('is-valid', 'import-field-optional-warning');
          this._findOptionalWarningTextsForLine(line, 'payer').forEach((t) => this._markOptionalWarningFixed(t));
          this._findOptionalWarningTextsForLine(line, 'insurance payer name is missing').forEach((t) => this._markOptionalWarningFixed(t));
        }
      } else if (fieldName === 'discipline' || fieldName === 'diagnosis codes') {
        const ok = this._validateDisciplineDiagnosisPair(field);
        if (ok) {
          field.classList.remove('import-input-border-warning', 'import-field-optional-warning', 'is-invalid');
          field.classList.add('is-valid', 'import-field-optional-warning');
          ['discipline', 'diagnosis', 'Select a discipline', 'semicolons'].forEach((sub) => {
            this._findOptionalWarningTextsForLine(line, sub).forEach((t) => this._markOptionalWarningFixed(t));
          });
          if (rowEl) {
            const disc = rowEl.querySelector('select[id^="p_discipline_"]');
            const diag = rowEl.querySelector('select[id^="p_diagnosis_codes_"]');
            if (disc) {
              disc.classList.remove('import-input-border-warning');
              disc.classList.add('is-valid', 'import-field-optional-warning');
            }
            if (diag) {
              diag.classList.remove('import-input-border-warning');
              diag.classList.add('is-valid', 'import-field-optional-warning');
            }
          }
        }
      }
    }

    this._updateSubmitButton();
  }

  _validateDisciplineDiagnosisPair(field) {
    const row = field.closest('.nested-fields');
    if (!row) return true;
    const disc = row.querySelector('select[id^="p_discipline_"]');
    const diag = row.querySelector('select[id^="p_diagnosis_codes_"]');
    const hasDisc = disc && this._selectizeValue(disc).length > 0;
    const hasDiag = diag && this._diagnosisHasSelections(diag);

    // CSV had ICD codes but no valid discipline; importer drops codes so both fields can load empty.
    // Do not treat as "fixed" until the user selects a discipline (see data-pair-discipline-warning on row).
    if (row.dataset.pairDisciplineWarning === 'true') return hasDisc;
    if (hasDiag && !hasDisc) return false;
    return true;
  }

  _validateSexRow(line) {
    const wrap = document.getElementById(`sex-field-${line}`);
    if (!wrap || wrap.dataset.hasWarning !== 'true') return;

    const male = document.getElementById(`sex_male_${line}`);
    const female = document.getElementById(`sex_female_${line}`);
    const na = document.getElementById(`sex_na_${line}`);
    const chosen = (male && male.checked) || (female && female.checked) || (na && na.checked);

    if (chosen) {
      wrap.classList.remove('sex-import-group--warning');
      wrap.classList.add('sex-import-group--resolved');
      this._findOptionalWarningTextsForLine(line, 'sex').forEach((t) => this._markOptionalWarningFixed(t));
      this._updateSubmitButton();
    }
  }

  // --- Error / warning marking ---

  _markRequiredErrorFixed(errorText) {
    if (this.fixedRequiredErrors.has(errorText)) return;
    this.fixedRequiredErrors.add(errorText);
    this._strikeGlobalRequiredError(errorText);
    const m = errorText.match(/Line (\d+):/);
    if (m) {
      this._strikeLineRequiredError(m[1], errorText);
      this._maybeHideLineRequiredBanner(m[1]);
    }
    this._updateSubmitButton();
  }

  _markOptionalWarningFixed(warningText) {
    if (this.fixedOptionalWarnings.has(warningText)) return;
    this.fixedOptionalWarnings.add(warningText);
    const m = warningText.match(/Line (\d+):/);
    const line = m ? m[1] : null;
    if (line) {
      const ul = document.getElementById(`line-${line}-warning-list`);
      if (ul) {
        ul.querySelectorAll('li').forEach((item) => {
          if (item.dataset.warningText === warningText) {
            item.style.textDecoration = 'line-through';
            item.style.opacity = '0.5';
          }
        });
      }
      this._maybeHideLineWarningBanner(line);
    }
    this._updateSubmitButton();
  }

  _strikeGlobalRequiredError(errorText) {
    const errorList = document.getElementById('error-list');
    if (!errorList) return;
    errorList.querySelectorAll('li').forEach((item) => {
      if (item.dataset.errorText === errorText) {
        item.style.textDecoration = 'line-through';
        item.style.opacity = '0.5';
        const link = item.querySelector('a');
        if (link) link.style.textDecoration = 'line-through';
      }
    });
  }

  _strikeLineRequiredError(line, errorText) {
    const ul = document.getElementById(`line-${line}-required-list`);
    if (!ul) return;
    ul.querySelectorAll('li').forEach((item) => {
      if (item.dataset.errorText === errorText) {
        item.style.textDecoration = 'line-through';
        item.style.opacity = '0.5';
      }
    });
  }

  _maybeHideLineRequiredBanner(line) {
    const ul = document.getElementById(`line-${line}-required-list`);
    if (!this._allItemsStruck(ul)) return;
    const banner = document.getElementById(`line-${line}-error-banner`);
    if (banner) banner.classList.add('d-none');
  }

  _maybeHideLineWarningBanner(line) {
    const ul = document.getElementById(`line-${line}-warning-list`);
    if (!this._allItemsStruck(ul)) return;
    const banner = document.getElementById(`line-${line}-warning-banner`);
    if (banner) banner.classList.add('d-none');
  }

  _allItemsStruck(ul) {
    if (!ul) return true;
    const items = ul.querySelectorAll('li');
    if (items.length === 0) return true;
    return Array.from(items).every((li) => {
      if (window.getComputedStyle(li).textDecorationLine === 'line-through') return true;
      return li.style.textDecoration && li.style.textDecoration.includes('line-through');
    });
  }

  // --- DOM finders ---

  _findRequiredErrorTextsForLineField(line, fieldName) {
    const prefix = `Line ${line}: ${fieldName}`;
    const out = [];
    this.element.querySelectorAll('#error-list li[data-error-text]').forEach((li) => {
      const t = li.dataset.errorText;
      if (t && t.indexOf(prefix) === 0) out.push(t);
    });
    return out;
  }

  _findOptionalWarningTextsForLine(line, substring) {
    const out = [];
    const ul = document.getElementById(`line-${line}-warning-list`);
    if (!ul) return out;
    ul.querySelectorAll('li[data-warning-text]').forEach((li) => {
      const t = li.dataset.warningText;
      if (t && t.indexOf(`Line ${line}:`) === 0 && t.includes(substring)) out.push(t);
    });
    return out;
  }

  // --- Selectize helpers ---

  _selectizeValue(selectEl) {
    if (!selectEl || selectEl.tagName !== 'SELECT') return '';
    if (selectEl.selectize) {
      const v = selectEl.selectize.getValue();
      if (v === null || v === undefined) return '';
      if (Array.isArray(v)) return v.length ? v.join(',') : '';
      return String(v);
    }
    return (selectEl.value || '').trim();
  }

  _diagnosisHasSelections(diagSelect) {
    if (!diagSelect) return false;
    if (diagSelect.selectize) {
      const v = diagSelect.selectize.getValue();
      if (Array.isArray(v)) return v.length > 0;
      return v != null && String(v).length > 0;
    }
    return diagSelect.selectedOptions && diagSelect.selectedOptions.length > 0;
  }

  _wireSelectizeValidation() {
    this.element.querySelectorAll('select.validation-field').forEach((el) => {
      if (!el.selectize) return;

      // MicroEvent (Selectize's event system) matches event names literally — namespaces are not
      // supported. Remove any previously registered handler before adding a new one.
      if (this.selectizeHandlers.has(el)) {
        el.selectize.off('change', this.selectizeHandlers.get(el));
      }

      const handler = () => {
        // Let Selectize finish updating native value / items before we read getValue().
        setTimeout(() => {
          this._validateField(el);
          if (el.dataset.field === 'discipline' || el.dataset.field === 'diagnosis codes') {
            const row = el.closest('.nested-fields');
            if (row) {
              const other = el.dataset.field === 'discipline'
                ? row.querySelector('select[id^="p_diagnosis_codes_"]')
                : row.querySelector('select[id^="p_discipline_"]');
              if (other && other !== el) this._validateField(other);
            }
          }
        }, 0);
      };

      this.selectizeHandlers.set(el, handler);
      el.selectize.on('change', handler);
    });
  }

  _syncValidationFromCurrentValues() {
    this.element.querySelectorAll('input.validation-field, select.validation-field').forEach((field) => {
      if (field.dataset.hasError === 'true' || field.dataset.hasWarning === 'true') {
        this._validateField(field);
      }
    });
    this.element.querySelectorAll('.sex-import-group[data-has-warning="true"]').forEach((wrap) => {
      const { line } = wrap.dataset;
      if (line) this._validateSexRow(line);
    });
  }

  _initSelectizeHooks() {
    this._wireSelectizeValidation();
    this._syncValidationFromCurrentValues();
  }

  _scheduleSelectizeHooks() {
    if (this.selectizeRetryTimers) {
      this.selectizeRetryTimers.forEach((id) => clearTimeout(id));
    }
    // Selectize may not have initialized yet when connect() runs; retry at increasing intervals.
    this.selectizeRetryTimers = [0, 120, 350, 700, 1200].map((ms) =>
      setTimeout(() => this._initSelectizeHooks(), ms)
    );
  }

  // --- Submit button ---

  _updateSubmitButton() {
    const submitBtn = this.element.querySelector('input[type="submit"]');
    if (!submitBtn) return;

    const remaining = this.totalRequiredErrorsValue - this.fixedRequiredErrors.size;
    const countEl = document.getElementById('error-count');
    const alertBox = document.getElementById('bulk-import-required-alert');

    if (countEl && this.totalRequiredErrorsValue > 0) {
      countEl.textContent = remaining === 1 ? '1 error' : `${remaining} errors`;
    }

    if (alertBox && remaining === 0 && this.totalRequiredErrorsValue > 0) {
      alertBox.classList.remove('alert-danger');
      alertBox.classList.add('alert-success');
      const h = alertBox.querySelector('.alert-heading');
      if (h) {
        h.innerHTML = '<i class="bi bi-check-circle"></i> All required issues are fixed. You can save your patients.';
      }
    }

    if (remaining > 0) {
      submitBtn.disabled = true;
      submitBtn.classList.remove('btn-primary');
      submitBtn.classList.add('btn-secondary');
      submitBtn.value = `Fix ${remaining} required error${remaining !== 1 ? 's' : ''} to continue`;
    } else {
      submitBtn.disabled = false;
      submitBtn.classList.add('btn-primary');
      submitBtn.classList.remove('btn-secondary');
      submitBtn.value = 'Save patients';
    }
  }
}
